import { SupabaseClient } from "@supabase/supabase-js";
import { Patient } from "./types";
import { MergeAuditLog } from "../../AuditLog/AuditLogList/types";

const supabasePatientRequest = `
*,
patient_addresses (
  type,
  address
),
legal_guardian:legal_guardian_id (
  id,
  first_name,
  last_name
),
family_contact:family_contact_id (
  id,
  first_name,
  last_name
),
appointment_patient_details (
  appointment:appointment_id (
    id,
    status,
    center:center_id (
      name,
      timezone,
      region
    ),
    time_slot:time_slot_id (
      started_at
    ),
    appointment_offerings (
      side,
      center_offering:center_offering_id (
        name
      )
    ),
    appointment_packages (
      center_package: center_package_id (
        name
      )
    )
  )
),
patient_forms (
  id,
  type,
  patient_signed_at,
  expiration_at,
  file_name,
  patient_id
),
patient_audit_logs (
  id,
  created_at,
  fields,
  role,
  patient_id,
  customer: customer_id (
    first_name,
    last_name
  ),
  center_user: center_user_id (
    first_name,
    last_name
  )
)
`;

// Patient Audit AC1 — merge_audit_logs.record_id is a polymorphic reference
// (customer or patient merges share the same table), so there's no FK for
// Supabase to embed it via a nested select on patients — fetch it
// separately, mirroring getCustomerData.ts's getCustomerMergeAuditLogs.
const getPatientMergeAuditLogs = async (
  supabase: SupabaseClient,
  id: string,
) => {
  const { data, error } = await supabase
    .from("merge_audit_logs")
    .select(
      "id, created_at, merge_type, role, target_record_id, merge_count, performed_by_name, field_changes",
    )
    .eq("record_id", id)
    .eq("merge_type", "patient")
    .order("created_at", { ascending: false })
    .returns<MergeAuditLog[]>();

  if (error) throw new Error(error.message);

  return data ?? [];
};

const getPatientData = async ({
  mrn,
  supabase,
}: {
  mrn: string;
  supabase: SupabaseClient;
}) => {
  const { data, error } = await supabase
    .from("patients")
    .select(supabasePatientRequest)
    .eq("mrn", mrn)
    .is(
      "appointment_patient_details.appointment.appointment_offerings.appointment_package_id" as keyof Patient,
      null,
    )
    .limit(1)
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    .order("created_at" as any, {
      foreignTable: "patient_audit_logs",
      ascending: false,
    })
    .maybeSingle<Patient>();

  if (error) throw new Error(error.message);
  if (!data) return data;

  // record_id is only known once the main query resolves — can't run this
  // in parallel with it (unlike getCustomerData.ts, whose customerId is
  // already known from the route).
  const mergeAuditLogs = await getPatientMergeAuditLogs(supabase, data.id);

  return { ...data, merge_audit_logs: mergeAuditLogs };
};

export default getPatientData;
